import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import {
  Row,
  Col,
  Card,
  Select,
  Typography,
  Button,
  Form,
  message,
  Spin,
  List,
  Space,
  Empty,
  Tag,
  Avatar,
  Tooltip,
  Input,
} from "antd";
import styled from "styled-components";
import {
  CustomTitle,
  CustomSubTitle,
  CustomButton,
  CustomSecondaryButton,
  PhysicianPageWrapper,
} from "../physician_page/styled";
import {
  SearchOutlined,
  PlayCircleOutlined,
  SaveOutlined,
  FileTextOutlined,
  EditOutlined,
  RobotOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  TeamOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import { debounce, notify } from "../../../utils/helper";
import scriptService from "../../../services/script";
import { physicianService } from "../../../services/user";
import TextArea from "antd/lib/input/TextArea";
import { BsStars } from "react-icons/bs";
import GenereateScriptWithAi from "../admin/scripts/GenerateScriptWithAi";
import { extractToken } from "../../../utils/localStorage";
import { globalRoles } from "../../../utils/roles";
import { SpecilityContext } from "../../../context/SpecilityContext";
import DeleteConfirmationModal from "../physician_page/component/DeleteConfirmationModal";
import { AnimatedDeleteButton } from "../videos_page/styled";
import { capitalizeFirstLetter } from "../../../utils/utils";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const statusOptions = [
  { label: "All", value: "All" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const ScriptsPage = () => {
  const [scripts, setScripts] = useState([]);
  const [allScripts, setAllScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [savingScript, setSavingScript] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [scriptsPage, setScriptsPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [hasMoreScripts, setHasMoreScripts] = useState(true);
  const [editingScript, setEditingScript] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [form] = Form.useForm();

  const user = extractToken();

  const [physicians, setPhysicians] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [viewingScript, setViewingScript] = useState(null);
  const [assignedMembers, setAssignedMembers] = useState({});
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [openScriptAiModel, setOpenScriptAiModel] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState(null);

  // Create debounced search function outside of component renders
  const debouncedSearchRef = useRef(null);

  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce((searchValue, status) => {
      setSearchText(searchValue);
      performSearch(searchValue, status);
    }, 500);
  }

  const performSearch = async (searchValue, status) => {
    setScriptsPage(1);
    setAllScripts([]);
    getAllScripts(1, searchValue, status, false);
  };

  const getAllScripts = useCallback(
    async (
      page = 1,
      searchInput = searchText,
      status = selectedStatus,
      isLoadMore = false
    ) => {
      if (isLoadMore) {
        setLoadMoreLoading(true);
      } else {
        setScriptsLoading(true);
      }

      try {
        let statusFilter = status === "All" ? "" : status;
        const response = await scriptService.getScripts(
          pageSize,
          page,
          searchInput,
          "",
          statusFilter
        );

        const newScripts = response?.data?.results || [];

        if (isLoadMore) {
          setAllScripts((prevScripts) => [...prevScripts, ...newScripts]);
          setScripts(response?.data);
        } else {
          setAllScripts(newScripts);
          setScripts(response?.data);
        }

        setHasMoreScripts(newScripts.length === pageSize);
        setScriptsPage(page);

        const newAssignedMembers = {};
        newScripts.forEach((script) => {
          if (script.assignedTo && script.assignedTo.length > 0) {
            newAssignedMembers[script.id] = script.assignedTo;
          }
        });
        setAssignedMembers((prev) => ({ ...prev, ...newAssignedMembers }));
      } catch (error) {
        notify("error", "Failed to load scripts");
      } finally {
        if (isLoadMore) {
          setLoadMoreLoading(false);
        } else {
          setScriptsLoading(false);
        }
      }
    },
    [selectedStatus, pageSize]
  );

  const getScriptById = async (id) => {
    try {
      const response = await scriptService.getScript(id);
      return response?.data;
    } catch (error) {
      notify("error", "Failed to load script details");
      return null;
    }
  };

  const fetchPhysicians = async () => {
    try {
      setLoading(true);
      const response = await physicianService.getAll(0,1,"","","");
      setPhysicians(response?.data?.results || []);

      const boardMembersList = (response?.data?.results || []).filter(
        (physician) => physician.boardMember === true
      );
      setBoardMembers(boardMembersList);
    } catch (error) {
      notify("error", "Failed to load physicians");
    } finally {
      setLoading(false);
    }
  };

  const handleEditScript = async (id) => {
    setScriptsLoading(true);
    try {
      const script = await getScriptById(id);
      if (script) {
        setEditingScript(script);
        setViewingScript(id);
        setEditMode(true);
        setViewMode("detail");
        // Handle different possible data structures for board member
        let boardMemberId = undefined;

        // Check for boardMember object first (as seen in list view)
        if (script.boardMember && script.boardMember.id) {
          boardMemberId = script.boardMember.id;
        } else if (script.assignedTo) {
          if (Array.isArray(script.assignedTo)) {
            boardMemberId = script.assignedTo[0];
          } else if (script.assignedTo.id) {
            boardMemberId = script.assignedTo.id;
          } else if (typeof script.assignedTo === "string") {
            boardMemberId = script.assignedTo;
          } else if (script.assignedTo.boardMember) {
            boardMemberId = script.assignedTo.boardMember;
          }
        }

        form.setFieldsValue({
          title: script.title,
          content: script.content,
          boardMembers: boardMemberId,
        });
      }
    } catch (error) {
      notify("error", "Error setting up script edit");
    } finally {
      setScriptsLoading(false);
    }
  };

  const handleViewScript = async (id) => {
    setScriptsLoading(true);
    try {
      const script = await getScriptById(id);

      if (script) {
        setEditingScript(script);
        setViewingScript(id);
        setEditMode(false);
        setViewMode("detail");
        // Handle different possible data structures for board member
        let boardMemberId = undefined;

        // Check for boardMember object first (as seen in list view)
        if (script.boardMember && script.boardMember.id) {
          boardMemberId = script.boardMember.id;
        } else if (script.assignedTo) {
          if (Array.isArray(script.assignedTo)) {
            boardMemberId = script.assignedTo[0];
          } else if (script.assignedTo.id) {
            boardMemberId = script.assignedTo.id;
          } else if (typeof script.assignedTo === "string") {
            boardMemberId = script.assignedTo;
          } else if (script.assignedTo.boardMember) {
            boardMemberId = script.assignedTo.boardMember;
          }
        }

        form.setFieldsValue({
          title: script.scriptTitle,
          content: script.scriptContent,
          boardMembers: boardMemberId,
        });

        if (script.assignedTo) {
          setAssignedMembers((prev) => ({
            ...prev,
            [script.id]: script.assignedTo,
          }));
        }
      }
    } catch (error) {
      notify("error", "Error loading script details");
    } finally {
      setScriptsLoading(false);
    }
  };

  const handleAssignScript = async (scriptId, memberIds) => {
    setSavingAssignments(true);
    try {
      setAssignedMembers((prev) => ({
        ...prev,
        [scriptId]: memberIds,
      }));
    } catch (error) {
      notify("error", "Failed to assign board member");
    } finally {
      setSavingAssignments(false);
    }
  };

  const handleDeleteScript = useCallback((id, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setScriptToDelete(id);
    setDeleteModalOpen(true);
  }, []);

  const confirmDeleteScript = async () => {
    try {
      await scriptService.deleteScript(scriptToDelete);
      notify("success", "Script deleted successfully");

      if (
        viewingScript === scriptToDelete ||
        (editingScript && editingScript.id === scriptToDelete)
      ) {
        setViewingScript(null);
        setEditingScript(null);
        setViewMode("list");
      }

      setScriptsPage(1);
      setAllScripts([]);
      getAllScripts(1, searchText, selectedStatus, false);
      setDeleteModalOpen(false);
      setScriptToDelete(null);
    } catch (error) {
      notify("error", "Failed to delete script");
    }
  };

  const handleCloseScriptDetail = useCallback(() => {
    setViewingScript(null);
    setEditingScript(null);
    setEditMode(false);
    setViewMode("list");
    form.resetFields();
  }, [form]);

  // Stable search change handler
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setInputValue(value);
      debouncedSearchRef.current(value, selectedStatus);
    },
    [selectedStatus]
  );

  const handleStatusChange = useCallback(
    (value) => {
      setSelectedStatus(value);
      setScriptsPage(1);
      setAllScripts([]);
      getAllScripts(1, searchText, value, false);
    },
    [searchText, getAllScripts]
  );

  const handleLoadMoreScripts = useCallback(() => {
    const nextPage = scriptsPage + 1;
    getAllScripts(nextPage, searchText, selectedStatus, true);
  }, [scriptsPage, searchText, selectedStatus, getAllScripts]);

  const handleSaveScript = async (values) => {
    setSavingScript(true);
    try {
      let scriptId;

      if (editMode && editingScript) {
        const updateResponse = await scriptService.updateScript(
          editingScript.id,
          {
            scriptTitle: values.title,
            scriptContent: values.content,
          }
        );

        scriptId = editingScript.id;
        notify("success", "Script updated successfully!");
      } else {
        const createResponse = await scriptService.createScript({
          scriptTitle: values.title,
          scriptContent: values.content,
        });

        scriptId = createResponse?.data?.script?.id;
        notify("success", "Script saved successfully!");
      }

      if (scriptId && values.boardMembers) {
        await assignScriptToBoardMember([values.boardMembers], scriptId);
      }

      form.resetFields();
      setEditingScript(null);
      setViewingScript(null);
      setEditMode(false);
      setViewMode("list");
      setScriptsPage(1);
      setAllScripts([]);
      getAllScripts(1, searchText, selectedStatus, false);
    } catch (error) {
      notify("error", "Failed to save script. Please try again.");
      console.error("Error saving script:", error);
    } finally {
      setSavingScript(false);
    }
  };

  const assignScriptToBoardMember = async (boardMemberIds, scriptId) => {
    try {
      if (!boardMemberIds || boardMemberIds.length === 0) return;

      const response = await scriptService.assignBoardMember({
        boardMember: boardMemberIds[0],
        scriptId: [scriptId],
      });

      setAssignedMembers((prev) => ({
        ...prev,
        [scriptId]: boardMemberIds,
      }));

      return response;
    } catch (error) {
      notify("error", "Failed to assign board members to script");
    }
  };

  const handleGenerateScript = () => {};

  const handleNewScript = useCallback(() => {
    setEditingScript(null);
    setViewingScript(null);
    setEditMode(true);
    setViewMode("detail");
    form.resetFields();
  }, [form]);

  useEffect(() => {
    setScriptsPage(1);
    setAllScripts([]);
    getAllScripts(1, searchText, selectedStatus, false);
    fetchPhysicians();
    setViewMode("list");
    setViewingScript(null);
    setEditingScript(null);
    setEditMode(false);
  }, [getAllScripts, searchText, selectedStatus]);

  const SearchInput = useMemo(
    () => (
      <Input
        placeholder="Search Scripts..."
        className="pr-40"
        value={inputValue}
        onChange={handleSearchChange}
      />
    ),
    [inputValue, handleSearchChange]
  );

  const ScriptsSidebar = useMemo(
    () => (
      <Col span={6}>
        <Card className="mb-15">
          <Col>
            <h3 className="font-24 text-primary">Scripts</h3>
          </Col>
          <Col className="mb-15">
            <CustomButton
              type="default"
              block
              onClick={handleNewScript}
              style={{
                backgroundColor: "#8a3ef4",
                color: "var(--primary-text-color)",
                borderColor: "#8a3ef4",
                height: "40px",
              }}
              icon={<PlusOutlined />}
            >
              New Script
            </CustomButton>
          </Col>

          {viewMode === "list" && (
            <>
              <Col>
                <h3 className="font-24 text-primary">Filters</h3>
              </Col>
              <Col className="mb-24">
                <div className="mb-5 font-16 font-semibold text-primary">
                  Search
                </div>
                <div className="row-flex">
                  {SearchInput}
                  <SearchOutlined className="videopage-search-icon font-19" />
                </div>
              </Col>
              <Col className="mb-24">
                <div className="mb-5 font-16 font-semibold text-primary">
                  Status
                </div>
                <div>
                  <Select
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    showSearch
                    style={{ width: "100%" }}
                    dropdownStyle={{
                      maxHeight: 200,
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                    listHeight={160}
                    className="w-100"
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    options={statusOptions}
                  />
                </div>
              </Col>
            </>
          )}

          {viewMode === "detail" && (
            <div
              style={{
                maxHeight: "500px",
                overflowY: "auto",
                marginTop: "15px",
              }}
            >
              {scriptsLoading ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <Spin />
                </div>
              ) : allScripts && allScripts.length > 0 ? (
                <List
                  itemLayout="horizontal"
                  dataSource={allScripts}
                  renderItem={(script) => (
                    <List.Item
                      style={{
                        padding: "10px",
                        borderBottom: "1px solid var(--border-color)",
                        backgroundColor:
                          viewingScript === script.id
                            ? "rgba(138, 62, 244, 0.05)"
                            : "transparent",
                        cursor: "pointer",
                      }}
                      onClick={() => handleViewScript(script.id)}
                    >
                      <List.Item.Meta
                        title={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span
                              style={{ color: "var(--primary-text-color)" }}
                            >
                              {script.scriptTitle}
                            </span>
                            <div
                              onClick={(e) => {
                                if (e && e.stopPropagation) e.stopPropagation();
                                handleDeleteScript(script.id, e);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <DeleteOutlined
                                style={{ color: "#ff4d4f", fontSize: "16px" }}
                              />
                            </div>
                          </div>
                        }
                        description={
                          <Text
                            type="secondary"
                            style={{ color: "var(--secondary-text-color)" }}
                          >
                            {script.updatedAt
                              ? new Date(script.updatedAt).toLocaleDateString()
                              : new Date(script.createdAt).toLocaleDateString()}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <Empty
                  description={
                    <span style={{ color: "var(--secondary-text-color)" }}>
                      No scripts found
                    </span>
                  }
                  style={{ padding: "20px" }}
                />
              )}
            </div>
          )}

          {viewMode === "detail" && hasMoreScripts && (
            <Col className="mt-15">
              <CustomButton
                block
                onClick={handleLoadMoreScripts}
                loading={loadMoreLoading}
                style={{
                  borderColor: "#8a3ef4",
                  color: "#8a3ef4",
                }}
              >
                Load More
              </CustomButton>
            </Col>
          )}
        </Card>
      </Col>
    ),
    [
      viewMode,
      handleNewScript,
      SearchInput,
      selectedStatus,
      handleStatusChange,
      scriptsLoading,
      allScripts,
      hasMoreScripts,
      loadMoreLoading,
      viewingScript,
      handleDeleteScript,
      handleLoadMoreScripts,
    ]
  );

  const ScriptsListView = useMemo(
    () => (
      <Col span={18}>
        <Card>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: "20px" }}
          >
            <Col>
              <Title
                level={4}
                style={{ margin: 0, color: "var(--primary-text-color)" }}
              >
                <FileTextOutlined style={{ marginRight: "10px" }} />
                All Scripts
              </Title>
            </Col>
          </Row>

          {scriptsLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
            </div>
          ) : allScripts && allScripts.length > 0 ? (
            <Row gutter={[16, 16]}>
              {allScripts.map((script) => (
                <Col span={24} key={script.id}>
                  <Card
                    hoverable
                    style={{ height: "100%" }}
                    onClick={() => handleViewScript(script.id)}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div>
                        <Title
                          level={5}
                          style={{
                            marginBottom: "8px",
                            color: "var(--primary-text-color)",
                          }}
                        >
                          {script.scriptTitle} :{" "}
                          {
                            <Tag
                              style={{
                                background:
                                  script.status === "pending"
                                    ? "#faad14"
                                    : script.status === "approved"
                                    ? "#52c41a"
                                    : script.status === "rejected"
                                    ? "#ff4d4f"
                                    : "#570BC1",
                                color: "white",
                                border: "none",
                              }}
                            >
                              {capitalizeFirstLetter(script.status)}
                            </Tag>
                          }
                        </Title>
                        <Text
                          type="secondary"
                          style={{ color: "var(--secondary-text-color)" }}
                        >
                          {script.updatedAt
                            ? new Date(script.updatedAt).toLocaleDateString()
                            : new Date(script.createdAt).toLocaleDateString()}
                        </Text>
                      </div>
                      {user?.role === globalRoles.SUPERADMIN && (
                        <AnimatedDeleteButton
                          onClick={(e) => {
                            if (e && e.stopPropagation) e.stopPropagation();
                            handleDeleteScript(script.id, e);
                          }}
                        >
                          <DeleteOutlined />
                          <span className="delete-text">Delete</span>
                        </AnimatedDeleteButton>
                      )}
                    </div>
                    {script?.boardMember?.id && (
                      <div style={{ marginTop: "10px" }}>
                        <TeamOutlined
                          style={{
                            marginRight: "5px",
                            color: "var(--secondary-text-color)",
                          }}
                        />

                        <Tag
                          style={{
                            backgroundColor: "#52c41a",
                            color: "white",
                            border: "none",
                            fontSize: "11px",
                          }}
                        >
                          {script?.boardMember?.name}
                        </Tag>
                      </div>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Empty
              description={
                <div style={{ color: "var(--secondary-text-color)" }}>
                  <p>No scripts found</p>
                  <CustomButton
                    type="primary"
                    onClick={handleNewScript}
                    style={{
                      backgroundColor: "#8a3ef4",
                      borderColor: "#8a3ef4",
                      marginTop: "15px",
                    }}
                  >
                    Create Your First Script
                  </CustomButton>
                </div>
              }
              style={{ padding: "60px" }}
            />
          )}
          {hasMoreScripts && (
            <Row justify="center" style={{ marginTop: "30px" }}>
              <Col>
                <CustomButton
                  onClick={handleLoadMoreScripts}
                  loading={loadMoreLoading}
                  style={{
                    borderColor: "#8a3ef4",
                    color: "#8a3ef4",
                  }}
                >
                  Load More Scripts
                </CustomButton>
              </Col>
            </Row>
          )}
          {!hasMoreScripts && allScripts.length > 0 && (
            <Row justify="center" style={{ marginTop: "30px" }}>
              <Col>
                <p style={{ color: "#666", fontStyle: "italic" }}>
                  No more scripts to load
                </p>
              </Col>
            </Row>
          )}
        </Card>
      </Col>
    ),
    [
      scriptsLoading,
      allScripts,
      hasMoreScripts,
      loadMoreLoading,
      handleViewScript,
      user?.role,
      handleDeleteScript,
      handleNewScript,
      handleLoadMoreScripts,
    ]
  );

  const ScriptDetailView = () => (
    <Col span={18}>
      <Card className="script-editor-card" style={{ padding: "20px" }}>
        <Row gutter={[0, 20]}>
          <Col span={24}>
            <div
              className="script-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <Title
                  level={4}
                  style={{ margin: 0, color: "var(--primary-text-color)" }}
                >
                  <EditOutlined style={{ marginRight: "10px" }} />
                  {editMode
                    ? editingScript
                      ? "Edit Script"
                      : "Create New Script"
                    : "View Script"}
                </Title>
                <Text
                  type="secondary"
                  style={{ color: "var(--secondary-text-color)" }}
                >
                  {editMode
                    ? editingScript
                      ? "Make changes to your script"
                      : "Create and save your video script"
                    : "Viewing script details"}
                </Text>
              </div>
              <div
                onClick={handleCloseScriptDetail}
                style={{
                  color: "var(--primary-text-color)",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                <CloseOutlined />
              </div>
            </div>
          </Col>
          {editMode && (
            <Col span={24}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  width: "100%",
                }}
              >
                <CustomButton
                  type="primary"
                  onClick={() => setOpenScriptAiModel(true)}
                  icon={<BsStars />}
                >
                  Generate Script By AI
                </CustomButton>
              </div>
            </Col>
          )}

          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveScript}
              initialValues={{
                title: "",
                content: "",
                boardMembers: undefined,
              }}
            >
              <Form.Item
                name="title"
                label={
                  <span style={{ color: "var(--primary-text-color)" }}>
                    Script Title
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: "Please enter a title for your script",
                  },
                ]}
              >
                <Input
                  placeholder="Enter script title..."
                  size="large"
                  disabled={!editMode && editingScript !== null}
                  style={{
                    backgroundColor: "var(--primary-bg)",
                    color: "var(--primary-text-color)",
                  }}
                />
              </Form.Item>

              <Form.Item
                name="content"
                label={
                  <span style={{ color: "var(--primary-text-color)" }}>
                    Script Content
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: "Please enter your script content",
                  },
                ]}
              >
                <TextArea
                  placeholder="Write your script here..."
                  autoSize={{ minRows: 12, maxRows: 12 }}
                  disabled={!editMode && editingScript !== null}
                  style={{
                    fontSize: "16px",
                    lineHeight: "1.6",
                    padding: "16px",
                    fontFamily: "'Courier New', Courier, monospace",
                    backgroundColor: "var(--primary-bg)",
                    color: "var(--primary-text-color)",
                    border: "1px solid var(--border-color)",
                  }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span style={{ color: "var(--primary-text-color)" }}>
                    <TeamOutlined style={{ marginRight: "8px" }} />
                    Assign to Board Members
                  </span>
                }
                name="boardMembers"
              >
                {editMode ? (
                  <Select
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    placeholder="Select a board member to assign this script"
                    showSearch
                    loading={loading}
                    disabled={!editMode && editingScript !== null}
                    onChange={(value) => {
                      if (editingScript) {
                        handleAssignScript(editingScript.id, [value]);
                      }
                    }}
                    // tagRender={(props) => {
                    //   const { label, value, closable, onClose } = props;
                    //   return (
                    //     <Tag
                    //       color="#8a3ef4"
                    //       closable={closable}
                    //       onClose={onClose}
                    //       style={{ marginRight: '3px' }}
                    //     >
                    //       {label}
                    //     </Tag>
                    //   );
                    // }}
                    style={{
                      backgroundColor: "var(--primary-bg)",
                      color: "var(--primary-text-color)",
                    }}
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {boardMembers.map((physician) => (
                      <Option
                        key={physician.id}
                        label={`${physician.name} : ${physician?.specialty} - ${physician?.email}`}
                        value={physician.id}
                        style={{borderBottom: "1px solid var(--border-color)"}}
                      >
                        {physician.name} : {physician?.specialty} - {physician?.email}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <div>
                    {(() => {
                      const memberId = form.getFieldValue("boardMembers");
                      if (memberId) {
                        const member = boardMembers.find(
                          (bm) => bm.id === memberId
                        );
                        return member ? (
                          <Tag
                            color="#8a3ef4"
                            style={{ marginRight: "8px", marginBottom: "6px" }}
                          >
                            {member.name}
                            {member.specialty ? ` : ${member.specialty}` : ""}
                          </Tag>
                        ) : (
                          <Text
                            type="secondary"
                            style={{
                              color: "var(--primary-text-color)",
                            }}
                          >
                            Board member not found
                          </Text>
                        );
                      } else {
                        return (
                          <Text
                            type="secondary"
                            style={{
                              color: "var(--primary-text-color)",
                            }}
                          >
                            No board member assigned
                          </Text>
                        );
                      }
                    })()}
                  </div>
                )}
              </Form.Item>

              {editMode && (
                <Form.Item style={{ marginBottom: 0 }}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <CustomSecondaryButton
                        type="default"
                        onClick={() => form.resetFields()}
                        style={{
                          marginRight: "10px",
                          backgroundColor: "var(--primary-bg)",
                          color: "var(--primary-text-color)",
                          border: "1px solid var(--border-color)",
                        }}
                      >
                        Clear
                      </CustomSecondaryButton>
                      {/* <CustomButton
                          icon={<RobotOutlined />}
                          onClick={handleGenerateScript}
                          loading={generatingScript}
                          style={{
                            backgroundColor: '#8a3ef4',
                            borderColor: '#8a3ef4',
                          }}
                        >
                          {generatingScript ? 'Generating...' : 'Generate Script'}
                        </CustomButton> */}
                    </Col>
                    <Col>
                      <CustomButton
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={savingScript}
                        style={{
                          backgroundColor: "#8a3ef4",
                          borderColor: "#8a3ef4",
                        }}
                      >
                        {editingScript ? "Update Script" : "Save Script"}
                      </CustomButton>
                    </Col>
                  </Row>
                </Form.Item>
              )}

              {!editMode && editingScript && (
                <Row justify="end" style={{ marginTop: "16px" }}>
                  <Col>
                    <CustomButton
                      type="primary"
                      onClick={() => setEditMode(true)}
                      style={{
                        backgroundColor: "#8a3ef4",
                        borderColor: "#8a3ef4",
                      }}
                    >
                      Edit This Script
                    </CustomButton>
                  </Col>
                </Row>
              )}
            </Form>
          </Col>
        </Row>
      </Card>
    </Col>
  );

  const ScriptsSection = () => {
    return viewMode === "list" ? ScriptsListView : <ScriptDetailView />;
  };

  const AiScriptUse = (aiResponse) => {
    form.setFieldsValue({
      title: aiResponse?.title,
      content: aiResponse?.description,
    });
  };
  return (
    <>
      {openScriptAiModel && (
        <GenereateScriptWithAi
          AiScriptUse={AiScriptUse}
          open={openScriptAiModel}
          setOpen={setOpenScriptAiModel}
        />
      )}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        onConfiem={confirmDeleteScript}
        loading={false}
        title="Delete Script"
        message="Are you sure you want to delete this script? This action cannot be undone."
      />
      <Row gutter={16}>
        {ScriptsSidebar}
        <ScriptsSection />
      </Row>
    </>
  );
};

export default ScriptsPage;
